import { ActionItemFieldsFragment } from '~graphql/generated/graphql'
import { getCleanEnumCapitalized } from '~utils/helpers'

const OFFICE_TASK_REQUEST_TYPE_LABELS: Record<string, string> = {
    triage_callback: 'Triage Callback',
    scheduling_help: 'Scheduling Help',
    refill_request: 'Refill Request',
    records_request: 'Records Request',
    estimate_request: 'Estimate Request',
    general_callback: 'General Callback',
    appt_help: 'Appointment Help',
}

const OFFICE_TASK_PRIORITY_LABELS: Record<string, string> = {
    low: 'Low',
    normal: 'Normal',
    high: 'High',
}

const UNKNOWN_ENTITY_VALUES = new Set(['unknown', 'none', 'n/a', 'null'])

type OfficeTaskPriorityColor = 'red' | 'orange' | 'blue' | 'gray'

export type ParsedOfficeTaskDescription = {
    rawDescription: string
    isAiGenerated: boolean
    requestType?: string
    requestTypeLabel?: string
    priority?: string
    priorityLabel?: string
    clientId?: string
    clientName?: string
    petId?: string
    petName?: string
    details?: string
}

export function parseOfficeTaskDescription(
    description?: string | null,
): ParsedOfficeTaskDescription | null {
    const trimmedDescription = normalizeString(description)
    if (!trimmedDescription) return null

    const aiCallHeaderMatch = trimmedDescription.match(
        /^\[AI Call(?: - ([^\]]+))?\]\s*(.*)$/,
    )

    if (!aiCallHeaderMatch) {
        return {
            rawDescription: trimmedDescription,
            isAiGenerated: false,
            details: trimmedDescription,
        }
    }

    const requestType = normalizeString(aiCallHeaderMatch[1])
    const remainder = normalizeString(aiCallHeaderMatch[2])

    const parsed: ParsedOfficeTaskDescription = {
        rawDescription: trimmedDescription,
        isAiGenerated: true,
        requestType,
        requestTypeLabel: getOfficeTaskRequestTypeLabel(requestType),
    }

    if (!remainder) {
        return parsed
    }

    if (!remainder.includes('|')) {
        parsed.details = parseSerializedDetails(remainder)
        return parsed
    }

    const segments = remainder
        .split('|')
        .map(segment => segment.trim())
        .filter(Boolean)

    segments.forEach(segment => {
        const separatorIndex = segment.indexOf(':')
        if (separatorIndex === -1) {
            if (!parsed.details) {
                parsed.details = parseSerializedDetails(segment)
            }
            return
        }

        const key = segment.slice(0, separatorIndex).trim().toLowerCase()
        const value = segment.slice(separatorIndex + 1).trim()

        switch (key) {
            case 'priority': {
                parsed.priority = normalizeString(value)
                parsed.priorityLabel = getOfficeTaskPriorityLabel(
                    parsed.priority,
                )
                return
            }
            case 'client': {
                parsed.clientId = normalizeEntityId(value)
                return
            }
            case 'pet': {
                parsed.petId = normalizeEntityId(value)
                return
            }
            case 'details': {
                parsed.details = parseSerializedDetails(value)
                return
            }
            default: {
                if (!parsed.details) {
                    parsed.details = parseSerializedDetails(segment)
                }
            }
        }
    })

    const searchableText = parsed.details || parsed.rawDescription
    parsed.clientName = extractClientName(searchableText)
    parsed.petName = extractPetName(searchableText)

    return parsed
}

export function getOfficeTaskRequestTypeLabel(requestType?: string): string {
    const normalizedRequestType = normalizeString(requestType)
    if (!normalizedRequestType) return 'Office Task'

    return (
        OFFICE_TASK_REQUEST_TYPE_LABELS[normalizedRequestType] ??
        getCleanEnumCapitalized(normalizedRequestType)
    )
}

export function getOfficeTaskPriorityLabel(priority?: string): string {
    const normalizedPriority = normalizeString(priority)
    if (!normalizedPriority) return 'Not Set'

    return (
        OFFICE_TASK_PRIORITY_LABELS[normalizedPriority] ??
        getCleanEnumCapitalized(normalizedPriority)
    )
}

export function getOfficeTaskPriorityColor(
    priority?: string,
): OfficeTaskPriorityColor {
    const normalizedPriority = normalizeString(priority)?.toLowerCase()

    if (normalizedPriority === 'high') {
        return 'red'
    }

    if (normalizedPriority === 'normal') {
        return 'orange'
    }

    if (normalizedPriority === 'low') {
        return 'blue'
    }

    return 'gray'
}

/**
 * Best-available callback number for an AI office task.
 * Prefers the originating call's caller phone, then the linked client's phone.
 */
export function getOfficeTaskCallbackPhone(
    actionItem: Pick<ActionItemFieldsFragment, 'originating_call'>,
): string | undefined {
    return (
        normalizeString(actionItem.originating_call?.caller_phone) ||
        normalizeString(actionItem.originating_call?.client?.phone)
    )
}

function normalizeEntityId(value?: string): string | undefined {
    const normalizedValue = normalizeString(value)
    if (!normalizedValue) return undefined

    if (UNKNOWN_ENTITY_VALUES.has(normalizedValue.toLowerCase())) {
        return undefined
    }

    return normalizedValue
}

function normalizeString(value?: string | null): string | undefined {
    if (!value) return undefined

    const trimmedValue = value.trim()
    if (!trimmedValue) return undefined

    return trimmedValue
}

function parseSerializedDetails(value: string): string {
    const normalizedValue = value.trim()
    if (!normalizedValue) return ''

    try {
        const parsed = JSON.parse(normalizedValue) as unknown
        if (typeof parsed === 'string') {
            return parsed.trim()
        }
        if (parsed && typeof parsed === 'object') {
            return JSON.stringify(parsed)
        }
    } catch {
        // If details aren't valid JSON, treat them as plain text.
    }

    if (
        normalizedValue.startsWith('"') &&
        normalizedValue.endsWith('"') &&
        normalizedValue.length > 1
    ) {
        return normalizedValue.slice(1, -1).trim()
    }

    return normalizedValue
}

function extractClientName(value: string): string | undefined {
    const patterns = [
        /\b[Cc]lient:\s*([^,;]+)/,
        /\b[Nn]ew client\s+([A-Z][A-Za-z'`.-]+(?:\s+[A-Z][A-Za-z'`.-]+){1,2})/,
        /\b[Ee]xisting client\s+([A-Z][A-Za-z'`.-]+(?:\s+[A-Z][A-Za-z'`.-]+){1,2})/,
        /\b[Cc]lient\s+is\s+([A-Z][A-Za-z'`.-]+(?:\s+[A-Z][A-Za-z'`.-]+){1,2})/,
        /\b[Cc]lient\s+([A-Z][A-Za-z'`.-]+(?:\s+[A-Z][A-Za-z'`.-]+){1,2})\s+(?:calling|called|wants|requesting|asked)\b/,
    ]

    for (const pattern of patterns) {
        const match = value.match(pattern)
        const normalizedMatch = normalizeEntityName(match?.[1])
        if (normalizedMatch) return normalizedMatch
    }

    return undefined
}

function extractPetName(value: string): string | undefined {
    const patterns = [
        /\b[Pp]et\s+['"]?([A-Z][A-Za-z'`.-]+)/,
        /\b[Pp]atient\s+['"]?([A-Z][A-Za-z'`.-]+)/,
        /\bfor\s+pet\s+['"]?([A-Z][A-Za-z'`.-]+)/,
    ]

    for (const pattern of patterns) {
        const match = value.match(pattern)
        const normalizedMatch = normalizeEntityName(match?.[1])
        if (normalizedMatch) return normalizedMatch
    }

    return undefined
}

function normalizeEntityName(value?: string): string | undefined {
    if (!value) return undefined

    const normalizedValue = value
        .replace(/["']/g, '')
        .replace(/\s+/g, ' ')
        .replace(/[.,;:]+$/g, '')
        .trim()
    const cleanedName = stripTrailingContextWords(normalizedValue)
    if (!cleanedName) return undefined

    if (UNKNOWN_ENTITY_VALUES.has(cleanedName.toLowerCase())) {
        return undefined
    }

    if (isLikelyIdValue(cleanedName)) {
        return undefined
    }

    return cleanedName
}

function isLikelyIdValue(value: string): boolean {
    if (
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
            value,
        )
    ) {
        return true
    }

    return /^[0-9a-f]+$/i.test(value) && value.length >= 16
}

function stripTrailingContextWords(value: string): string {
    const CONTEXT_WORDS = new Set([
        'calling',
        'called',
        'wants',
        'want',
        'requesting',
        'requested',
        'asks',
        'asked',
        'for',
        'to',
        'on',
        'at',
        'is',
    ])

    const words = value.split(' ')
    while (words.length > 1) {
        const lastWord = words[words.length - 1]?.toLowerCase()
        if (!lastWord || !CONTEXT_WORDS.has(lastWord)) break
        words.pop()
    }

    return words.join(' ')
}
